/**
 * MoCA (Montreal Cognitive Assessment) Scoring System
 * Maps game performance to MoCA cognitive domains
 * Total Score: 0-30 points
 * Cut-off: <26 suggests cognitive impairment
 */

export interface MoCADomainScore {
  domain: string;
  score: number;
  maxScore: number;
  percentage: number;
}

export interface MoCAAssessment {
  totalScore: number;
  maxScore: number;
  percentage: number;
  interpretation: string;
  domains: MoCADomainScore[];
  gamesAnalyzed: number;
  timestamp: string;
}

/**
 * MoCA Domain Mapping:
 * - Visuospatial/Executive: 5 points (Number Sequencing, Memory Palace)
 * - Naming: 3 points (Image Matching)
 * - Memory: 5 points (Memory Palace, Image Matching)
 * - Attention: 6 points (Tone Memory, Ticker Tape, Number Sequencing)
 * - Language: 3 points (Ticker Tape - reading/processing)
 * - Abstraction: 2 points (Pattern recognition across games)
 * - Delayed Recall: 5 points (Memory Palace)
 * - Orientation: 1 point (Time awareness - based on session consistency)
 */

interface GameResult {
  gameType: string;
  accuracyPercentage?: number;
  accuracy?: number;
  score?: number;
  completionTimeSeconds?: number;
  timeSeconds?: number;
  level?: number;
  round?: number;
  gridSize?: number;
  sequenceLength?: number;
  correctPlacements?: number;
  totalPlacements?: number;
  correctAnswers?: number;
  totalAnswers?: number;
  reactionTimeMs?: number;
}

/**
 * Calculate Visuospatial/Executive score (max 5 points)
 * Based on: Number Sequencing (spatial planning) + Memory Palace (spatial memory)
 */
function calculateVisuospatialScore(results: GameResult[]): number {
  const numberSeqResults = results.filter(r => r.gameType === 'NumberSequence');
  const memoryPalaceResults = results.filter(r => r.gameType === 'MemoryPalace');
  
  let score = 0;
  
  // Number Sequencing: spatial planning and sequencing (2.5 points)
  if (numberSeqResults.length > 0) {
    const avgAccuracy = numberSeqResults.reduce((sum, r) => 
      sum + (r.accuracyPercentage || r.accuracy || 0), 0) / numberSeqResults.length;
    
    if (avgAccuracy >= 90) score += 2.5;
    else if (avgAccuracy >= 75) score += 2;
    else if (avgAccuracy >= 60) score += 1.5;
    else if (avgAccuracy >= 40) score += 1;
    else if (avgAccuracy >= 20) score += 0.5;
  }
  
  // Memory Palace: spatial memory organization (2.5 points)
  if (memoryPalaceResults.length > 0) {
    const avgAccuracy = memoryPalaceResults.reduce((sum, r) => 
      sum + (r.accuracyPercentage || r.accuracy || 0), 0) / memoryPalaceResults.length;
    
    if (avgAccuracy >= 90) score += 2.5;
    else if (avgAccuracy >= 75) score += 2;
    else if (avgAccuracy >= 60) score += 1.5;
    else if (avgAccuracy >= 40) score += 1;
    else if (avgAccuracy >= 20) score += 0.5;
  }
  
  return Math.min(score, 5);
}

/**
 * Calculate Naming score (max 3 points)
 * Based on: Image Matching (object recognition and naming)
 */
function calculateNamingScore(results: GameResult[]): number {
  const imageMatchingResults = results.filter(r => r.gameType === 'ImageMatching');
  
  if (imageMatchingResults.length === 0) return 0;
  
  const avgAccuracy = imageMatchingResults.reduce((sum, r) => 
    sum + (r.accuracyPercentage || r.accuracy || 0), 0) / imageMatchingResults.length;
  
  if (avgAccuracy >= 90) return 3;
  if (avgAccuracy >= 75) return 2.5;
  if (avgAccuracy >= 60) return 2;
  if (avgAccuracy >= 40) return 1.5;
  if (avgAccuracy >= 20) return 1;
  return 0.5;
}

/**
 * Calculate Memory score (max 5 points)
 * Based on: Memory Palace (delayed recall) + Image Matching (recognition memory)
 */
function calculateMemoryScore(results: GameResult[]): number {
  const memoryPalaceResults = results.filter(r => r.gameType === 'MemoryPalace');
  const imageMatchingResults = results.filter(r => r.gameType === 'ImageMatching');
  
  let score = 0;
  
  // Memory Palace: encoding and recall (3 points)
  if (memoryPalaceResults.length > 0) {
    const avgAccuracy = memoryPalaceResults.reduce((sum, r) => 
      sum + (r.accuracyPercentage || r.accuracy || 0), 0) / memoryPalaceResults.length;
    
    if (avgAccuracy >= 90) score += 3;
    else if (avgAccuracy >= 75) score += 2.5;
    else if (avgAccuracy >= 60) score += 2;
    else if (avgAccuracy >= 40) score += 1.5;
    else if (avgAccuracy >= 20) score += 1;
    else score += 0.5;
  }
  
  // Image Matching: recognition memory (2 points)
  if (imageMatchingResults.length > 0) {
    const avgAccuracy = imageMatchingResults.reduce((sum, r) => 
      sum + (r.accuracyPercentage || r.accuracy || 0), 0) / imageMatchingResults.length;
    
    if (avgAccuracy >= 90) score += 2;
    else if (avgAccuracy >= 75) score += 1.5;
    else if (avgAccuracy >= 60) score += 1;
    else if (avgAccuracy >= 40) score += 0.5;
  }
  
  return Math.min(score, 5);
}

/**
 * Calculate Attention score (max 6 points)
 * Based on: Tone Memory (auditory attention) + Ticker Tape (sustained attention) + Number Sequencing (concentration)
 */
function calculateAttentionScore(results: GameResult[]): number {
  const toneMemoryResults = results.filter(r => r.gameType === 'ToneMemory');
  const tickerTapeResults = results.filter(r => r.gameType === 'TickerTape');
  const numberSeqResults = results.filter(r => r.gameType === 'NumberSequence');
  
  let score = 0;
  
  // Tone Memory: auditory working memory (2 points)
  if (toneMemoryResults.length > 0) {
    const avgAccuracy = toneMemoryResults.reduce((sum, r) => 
      sum + (r.accuracyPercentage || r.accuracy || 0), 0) / toneMemoryResults.length;
    const avgSequenceLength = toneMemoryResults.reduce((sum, r) => 
      sum + (r.sequenceLength || 0), 0) / toneMemoryResults.length;
    
    if (avgAccuracy >= 85 && avgSequenceLength >= 5) score += 2;
    else if (avgAccuracy >= 70 && avgSequenceLength >= 4) score += 1.5;
    else if (avgAccuracy >= 50) score += 1;
    else if (avgAccuracy >= 30) score += 0.5;
  }
  
  // Ticker Tape: sustained attention and processing speed (2 points)
  if (tickerTapeResults.length > 0) {
    const avgAccuracy = tickerTapeResults.reduce((sum, r) => 
      sum + (r.accuracyPercentage || r.accuracy || 0), 0) / tickerTapeResults.length;
    
    if (avgAccuracy >= 90) score += 2;
    else if (avgAccuracy >= 75) score += 1.5;
    else if (avgAccuracy >= 60) score += 1;
    else if (avgAccuracy >= 40) score += 0.5;
  }
  
  // Number Sequencing: concentration and mental tracking (2 points)
  if (numberSeqResults.length > 0) {
    const avgAccuracy = numberSeqResults.reduce((sum, r) => 
      sum + (r.accuracyPercentage || r.accuracy || 0), 0) / numberSeqResults.length;
    
    if (avgAccuracy >= 90) score += 2;
    else if (avgAccuracy >= 75) score += 1.5;
    else if (avgAccuracy >= 60) score += 1;
    else if (avgAccuracy >= 40) score += 0.5;
  }
  
  return Math.min(score, 6);
}

/**
 * Calculate Language score (max 3 points)
 * Based on: Ticker Tape (reading fluency and comprehension)
 */
function calculateLanguageScore(results: GameResult[]): number {
  const tickerTapeResults = results.filter(r => r.gameType === 'TickerTape');
  
  if (tickerTapeResults.length === 0) return 0;
  
  const avgAccuracy = tickerTapeResults.reduce((sum, r) => 
    sum + (r.accuracyPercentage || r.accuracy || 0), 0) / tickerTapeResults.length;
  
  // Also consider reaction time for fluency
  const avgReactionTime = tickerTapeResults.reduce((sum, r) => 
    sum + (r.reactionTimeMs || 0), 0) / tickerTapeResults.length;
  
  let score = 0;
  
  if (avgAccuracy >= 90 && avgReactionTime < 2000) score = 3;
  else if (avgAccuracy >= 80 && avgReactionTime < 3000) score = 2.5;
  else if (avgAccuracy >= 70) score = 2;
  else if (avgAccuracy >= 50) score = 1.5;
  else if (avgAccuracy >= 30) score = 1;
  else score = 0.5;
  
  return score;
}

/**
 * Calculate Abstraction score (max 2 points)
 * Based on: Pattern recognition across all games (meta-cognitive ability)
 */
function calculateAbstractionScore(results: GameResult[]): number {
  if (results.length === 0) return 0;
  
  // Measure improvement over time (learning and pattern recognition)
  const gameTypes = Array.from(new Set(results.map(r => r.gameType)));
  let improvementScore = 0;
  
  gameTypes.forEach(gameType => {
    const gameResults = results.filter(r => r.gameType === gameType)
      .sort((a, b) => (a.level || a.round || 0) - (b.level || b.round || 0));
    
    if (gameResults.length >= 3) {
      const firstThird = gameResults.slice(0, Math.floor(gameResults.length / 3));
      const lastThird = gameResults.slice(-Math.floor(gameResults.length / 3));
      
      const avgFirst = firstThird.reduce((sum, r) => 
        sum + (r.accuracyPercentage || r.accuracy || 0), 0) / firstThird.length;
      const avgLast = lastThird.reduce((sum, r) => 
        sum + (r.accuracyPercentage || r.accuracy || 0), 0) / lastThird.length;
      
      const improvement = avgLast - avgFirst;
      if (improvement > 10) improvementScore += 0.5;
      else if (improvement > 5) improvementScore += 0.3;
    }
  });
  
  return Math.min(improvementScore, 2);
}

/**
 * Calculate Delayed Recall score (max 5 points)
 * Based on: Memory Palace (delayed recall performance)
 */
function calculateDelayedRecallScore(results: GameResult[]): number {
  const memoryPalaceResults = results.filter(r => r.gameType === 'MemoryPalace');
  
  if (memoryPalaceResults.length === 0) return 0;
  
  // Focus on accuracy after memorization period
  const avgAccuracy = memoryPalaceResults.reduce((sum, r) => 
    sum + (r.accuracyPercentage || r.accuracy || 0), 0) / memoryPalaceResults.length;
  
  if (avgAccuracy >= 90) return 5;
  if (avgAccuracy >= 80) return 4;
  if (avgAccuracy >= 70) return 3;
  if (avgAccuracy >= 60) return 2;
  if (avgAccuracy >= 40) return 1;
  return 0;
}

/**
 * Calculate Orientation score (max 1 point)
 * Based on: Consistent engagement and session timing awareness
 */
function calculateOrientationScore(results: GameResult[]): number {
  if (results.length === 0) return 0;
  
  // If patient completed multiple games consistently, award orientation point
  const gameTypes = new Set(results.map(r => r.gameType));
  const avgAccuracy = results.reduce((sum, r) => 
    sum + (r.accuracyPercentage || r.accuracy || 0), 0) / results.length;
  
  // Award point if patient shows consistent engagement (3+ different games, avg accuracy > 30%)
  if (gameTypes.size >= 3 && avgAccuracy > 30) return 1;
  if (gameTypes.size >= 2 && avgAccuracy > 40) return 1;
  
  return 0;
}

/**
 * Calculate complete MoCA assessment from game results
 */
export function calculateMoCAScore(results: GameResult[]): MoCAAssessment {
  if (!results || results.length === 0) {
    return {
      totalScore: 0,
      maxScore: 30,
      percentage: 0,
      interpretation: 'No data available',
      domains: [],
      gamesAnalyzed: 0,
      timestamp: new Date().toISOString()
    };
  }
  
  const domains: MoCADomainScore[] = [
    {
      domain: 'Visuospatial/Executive',
      score: calculateVisuospatialScore(results),
      maxScore: 5,
      percentage: 0
    },
    {
      domain: 'Naming',
      score: calculateNamingScore(results),
      maxScore: 3,
      percentage: 0
    },
    {
      domain: 'Memory',
      score: calculateMemoryScore(results),
      maxScore: 5,
      percentage: 0
    },
    {
      domain: 'Attention',
      score: calculateAttentionScore(results),
      maxScore: 6,
      percentage: 0
    },
    {
      domain: 'Language',
      score: calculateLanguageScore(results),
      maxScore: 3,
      percentage: 0
    },
    {
      domain: 'Abstraction',
      score: calculateAbstractionScore(results),
      maxScore: 2,
      percentage: 0
    },
    {
      domain: 'Delayed Recall',
      score: calculateDelayedRecallScore(results),
      maxScore: 5,
      percentage: 0
    },
    {
      domain: 'Orientation',
      score: calculateOrientationScore(results),
      maxScore: 1,
      percentage: 0
    }
  ];
  
  // Calculate percentages
  domains.forEach(domain => {
    domain.percentage = domain.maxScore > 0 ? (domain.score / domain.maxScore) * 100 : 0;
  });
  
  const totalScore = domains.reduce((sum, d) => sum + d.score, 0);
  const percentage = (totalScore / 30) * 100;
  
  // Interpretation based on MoCA cut-off scores
  let interpretation = '';
  if (totalScore >= 26) {
    interpretation = 'Normal cognitive function';
  } else if (totalScore >= 22) {
    interpretation = 'Mild cognitive impairment (MCI) suspected';
  } else if (totalScore >= 18) {
    interpretation = 'Moderate cognitive impairment';
  } else {
    interpretation = 'Severe cognitive impairment';
  }
  
  return {
    totalScore: Math.round(totalScore * 10) / 10, // Round to 1 decimal
    maxScore: 30,
    percentage: Math.round(percentage * 10) / 10,
    interpretation,
    domains,
    gamesAnalyzed: new Set(results.map(r => r.gameType)).size,
    timestamp: new Date().toISOString()
  };
}

/**
 * Get MoCA interpretation with additional context
 */
export function getMoCAInterpretation(score: number): {
  level: string;
  description: string;
  recommendations: string[];
} {
  if (score >= 26) {
    return {
      level: 'Normal',
      description: 'Cognitive performance within normal range',
      recommendations: [
        'Continue regular cognitive activities',
        'Maintain healthy lifestyle habits',
        'Regular check-ups recommended'
      ]
    };
  } else if (score >= 22) {
    return {
      level: 'MCI',
      description: 'Mild Cognitive Impairment suspected',
      recommendations: [
        'Clinical evaluation recommended',
        'Increase cognitive training activities',
        'Monitor progression over time',
        'Consider lifestyle modifications'
      ]
    };
  } else if (score >= 18) {
    return {
      level: 'Moderate',
      description: 'Moderate cognitive impairment detected',
      recommendations: [
        'Immediate clinical consultation recommended',
        'Comprehensive neurological assessment needed',
        'Consider cognitive rehabilitation programs',
        'Caregiver support may be beneficial'
      ]
    };
  } else {
    return {
      level: 'Severe',
      description: 'Severe cognitive impairment detected',
      recommendations: [
        'Urgent clinical evaluation required',
        'Comprehensive care plan needed',
        'Caregiver support essential',
        'Consider specialized cognitive interventions'
      ]
    };
  }
}
